import { getErrorMessage } from "../utils/errorMessage";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { mayPerformWithRole } from "../utils/userAccess";
import { trackersAPI, employeesAPI } from "../api/client";
import "./payrolltracker.css";
import ConfirmModal from "../components/ConfirmModal";

const BILLING_TYPES = ["Hourly", "Daily", "Monthly"];
const BILLING_STATUSES = ["Billable", "Non-Billable", "Manager", "Bench", "Shadow Resource", "Product Development"];
const EMPLOYMENT_TYPES = ["Consultant", "Employee", "Trainee", "Internal", "PT"];
const BUSINESS_UNIT_OPTIONS = ["IT", "HR", "Finance"];
const DIVISION_OPTIONS = ["Software", "Recruitment", "Accounting"];

const initialPayrollForm = {
  employeeNumber: "",
  refId: "",
  name: "",
  doj: "",
  mobileNo: "",
  ptDate: "",
  businessUnit: "",
  division: "",
  department: "",
  designation: "",
  customerName: "",
  location: "",
  billingType: "",
  billingRate: "",
  salaryPaidByCustomer: "",
  adminExpenditure: "",
  billedToCustomer: "",
  remarks: "",
  endClient: "",
  reportingManagerName: "",
  reportingManagerEmail: "",
  billingStatus: "",
  employmentType: "",
  poEntries: [{ poNumber: "", poStartDate: "", poEndDate: "", status: "Active", benchStartDate: "", remarks: "", attachmentFile: null, attachmentExisting: null }],
  billingEntries: [{ customerName: "", endClient: "", billingType: "", billingStatus: "", billingRate: "", salaryPaidByCustomer: "", adminExpenditure: "", billedToCustomer: "" }],
};

export default function PayrollTracker() {
  const [payrollData, setPayrollData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialPayrollForm);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [viewingOnly, setViewingOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter1, setFilter1] = useState("");
  const [filter2, setFilter2] = useState("");
  const [filter3, setFilter3] = useState("");
  const [filterBillingStatus, setFilterBillingStatus] = useState("");
  const [filterEmploymentType, setFilterEmploymentType] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [reportTab, setReportTab] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastInfo, setLastInfo] = useState("");
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [empStatusFilter, setEmpStatusFilter] = useState("");
  const [businessUnitOptions, setBusinessUnitOptions] = useState(BUSINESS_UNIT_OPTIONS);
  const [divisionOptions, setDivisionOptions] = useState(DIVISION_OPTIONS);
  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(1);
  const rowsPerPage = 20;
  const employeeDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const formSnapshotRef = useRef(null);

  useEffect(() => {
    const closeExportDropdownOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        exportDropdownRef.current.open = false;
      }
    };
    document.addEventListener("mousedown", closeExportDropdownOutside);
    return () => document.removeEventListener("mousedown", closeExportDropdownOutside);
  }, []);

  const getCurrentUserRole = () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        return decoded?.role || "unknown";
      }
    } catch { /* ignore */ }
    return "unknown";
  };

  const userRole = getCurrentUserRole();
  const canEdit = useMemo(() => mayPerformWithRole(userRole, "payroll", "edit", "payroll.edit"), [userRole]);
  const canExport = useMemo(() => mayPerformWithRole(userRole, "payroll", "export", "payroll.export"), [userRole]);

  const isFormDirty = useCallback(() => {
    if (!formSnapshotRef.current) return false;
    return JSON.stringify(formData) !== JSON.stringify(formSnapshotRef.current);
  }, [formData]);

  const confirmClose = useCallback(() => {
    if (isFormDirty() && !window.confirm("You have unsaved changes. Are you sure you want to close?")) return;
    setShowForm(false);
    setViewingOnly(false);
    formSnapshotRef.current = null;
  }, [isFormDirty]);

  const loadPayrollEntries = async () => {
    setError("");
    setLoading(true);
    try {
      const response = await trackersAPI.getPayrollRoster();
      const rows = Array.isArray(response.data) ? response.data : [];
      setPayrollData(rows);
    } catch (err) {
      console.error("Failed to load revenue roster", err);
      setError("Unable to load revenue tracker records from server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPayrollEntries(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await employeesAPI.getFilterValues();
        const data = res.data || {};
        if (Array.isArray(data.business_units) && data.business_units.length > 0) {
          setBusinessUnitOptions([...new Set(data.business_units.map((b) => String(b)).filter((b) => b.trim() !== ""))]);
        }
        if (Array.isArray(data.divisions) && data.divisions.length > 0) {
          setDivisionOptions([...new Set(data.divisions.map((d) => String(d)).filter((d) => d.trim() !== ""))]);
        }
      } catch (err) {
        console.error("Failed to fetch employee filter values", err);
      }
    })();
  }, []);

  const loadEmployeeOptions = useCallback(async (search, status = empStatusFilter) => {
    if (!search || search.length < 1) {
      setEmployeeOptions([]);
      return;
    }
    try {
      const params = { limit: 50, search };
      if (status) params.status = status;
      const response = await employeesAPI.getAll(params);
      const employees = Array.isArray(response.data?.employees) ? response.data.employees : [];
      setEmployeeOptions(employees);
    } catch (err) {
      console.error("Failed to load employee list", err);
      setEmployeeOptions([]);
    }
  }, [empStatusFilter]);

  useEffect(() => {
    if (!showEmployeeDropdown) return;
    const timer = setTimeout(() => { loadEmployeeOptions(employeeSearch); }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearch, loadEmployeeOptions, showEmployeeDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
        setShowEmployeeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (showForm && isFormDirty()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [showForm, isFormDirty]);

  const selectEmployee = (employeeId) => {
    const employee = employeeOptions.find((emp) => emp.employee_id === employeeId || String(emp.employee_id) === String(employeeId));
    if (!employee) return;
    setFormData((prev) => ({
      ...prev,
      employeeNumber: employee.employee_id || "",
      refId: employee.employee_id || "",
      name: employee.name || "",
      doj: employee.joining_date ? String(employee.joining_date).substring(0, 10) : "",
      mobileNo: employee.phone || "",
      location: employee.seating_location || employee.location || "",
      businessUnit: employee.business_unit || "",
      division: employee.division || "",
      department: employee.department || "",
      designation: employee.designation || "",
      reportingManagerName: employee.manager || "",
      employmentType: employee.employment_type || "",
    }));
    setShowEmployeeDropdown(false);
    setEmployeeSearch("");
    setEmployeeOptions([]);
  };

  const handleEmployeeSearchChange = (e) => {
    const val = e.target.value;
    setEmployeeSearch(val);
    setFormData((prev) => ({ ...prev, employeeNumber: val }));
    setShowEmployeeDropdown(true);
  };

  const customerOptions = useMemo(() => {
    return [...new Set(payrollData.map((item) => item.customerName).filter(Boolean))].sort();
  }, [payrollData]);

  const filteredData = useMemo(() => {
    return payrollData.filter((item) => {
      const poNumbers = (item.poEntries || []).map((pe) => pe.poNumber || "").join(" ");
      const searchable = [
        item.refId, item.employeeNumber, item.name, item.customerName,
        item.endClient, item.location, poNumbers,
        item.reportingManagerName, item.reportingManagerEmail,
      ].join(" ").toLowerCase();
      const matchesSearch = searchable.includes(searchTerm.toLowerCase());
      const latestStatus = (item.poEntries && item.poEntries.length > 0) ? item.poEntries[item.poEntries.length - 1].status : item.status;
      const matchesTab =
        reportTab === "all" ||
        (reportTab === "revenue" && item.hasRecord);
      const matchesFilter =
        (!filter1 || latestStatus === filter1) &&
        (!filter2 || item.billingType === filter2) &&
        (!filter3 || item.businessUnit === filter3) &&
        (!filterBillingStatus || item.billingStatus === filterBillingStatus) &&
        (!filterEmploymentType || item.employmentType === filterEmploymentType) &&
        (!filterCustomer || item.customerName === filterCustomer);
      return matchesSearch && matchesTab && matchesFilter;
    });
  }, [payrollData, searchTerm, reportTab, filter1, filter2, filter3, filterBillingStatus, filterEmploymentType, filterCustomer]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const pageData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page]);

  useEffect(() => { setPage(1); }, [searchTerm, reportTab, filter1, filter2, filter3, filterBillingStatus, filterEmploymentType, filterCustomer]);

  const handleAddNew = () => {
    if (!canEdit) return;
    const newFormData = { ...initialPayrollForm };
    formSnapshotRef.current = JSON.parse(JSON.stringify(newFormData));
    setFormData(newFormData);
    setShowForm(true);
    setViewingOnly(false);
    setEditingId(null);
    setLastInfo("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditRow = (row) => {
    if (!canEdit) return;
    setViewingOnly(false);
    const newData = {
      employeeNumber: row.employeeNumber || "",
      refId: row.refId || row.employeeNumber || "",
      name: row.name || "",
      doj: row.doj || "",
      mobileNo: row.mobileNo || "",
      ptDate: row.ptDate || "",
      businessUnit: row.businessUnit || "",
      division: row.division || "",
      department: row.department || "",
      designation: row.designation || "",
      customerName: row.customerName || "",
      location: row.location || "",
      billingType: row.billingType || "",
      billingRate: row.billingRate || "",
      salaryPaidByCustomer: row.salaryPaidByCustomer || "",
      adminExpenditure: row.adminExpenditure || "",
      billedToCustomer: row.billedToCustomer || "",
      remarks: row.remarks || "",
      endClient: row.endClient || "",
      reportingManagerName: row.reportingManagerName || "",
      reportingManagerEmail: row.reportingManagerEmail || "",
      billingStatus: row.billingStatus || "",
      employmentType: row.employmentType || "",
      poEntries: Array.isArray(row.poEntries) && row.poEntries.length > 0
        ? row.poEntries.map((pe) => ({
            id: pe.id || undefined,
            poNumber: pe.poNumber || "",
            poStartDate: pe.poStartDate || "",
            poEndDate: pe.poEndDate || "",
            status: pe.status || "Active",
            benchStartDate: pe.benchStartDate || "",
            remarks: pe.remarks || "",
            attachmentFile: null,
            attachmentExisting: pe.attachmentUrl ? { url: pe.attachmentUrl, name: pe.attachmentOriginalName } : null,
          }))
        : [{ poNumber: "", poStartDate: "", poEndDate: "", status: "Active", benchStartDate: "", remarks: "", attachmentFile: null, attachmentExisting: null }],
      billingEntries: Array.isArray(row.billingEntries) && row.billingEntries.length > 0
        ? row.billingEntries.map((be) => ({
            id: be.id || undefined,
            customerName: be.customerName || "",
            endClient: be.endClient || "",
            billingType: be.billingType || "",
            billingStatus: be.billingStatus || "",
            billingRate: be.billingRate ?? "",
            salaryPaidByCustomer: be.salaryPaidByCustomer ?? "",
            adminExpenditure: be.adminExpenditure ?? "",
            billedToCustomer: be.billedToCustomer ?? "",
          }))
        : [{ customerName: row.customerName || "", endClient: row.endClient || "", billingType: row.billingType || "", billingStatus: row.billingStatus || "", billingRate: row.billingRate ?? "", salaryPaidByCustomer: row.salaryPaidByCustomer ?? "", adminExpenditure: row.adminExpenditure ?? "", billedToCustomer: row.billedToCustomer ?? "" }],
    };
    formSnapshotRef.current = JSON.parse(JSON.stringify(newData));
    setFormData(newData);
    setEditingId(row.id);
    setShowForm(true);
  };

  const handleViewRow = (row) => {
    setViewingOnly(true);
    const newData = {
      employeeNumber: row.employeeNumber || "",
      refId: row.refId || row.employeeNumber || "",
      name: row.name || "",
      doj: row.doj || "",
      mobileNo: row.mobileNo || "",
      ptDate: row.ptDate || "",
      businessUnit: row.businessUnit || "",
      division: row.division || "",
      department: row.department || "",
      designation: row.designation || "",
      customerName: row.customerName || "",
      location: row.location || "",
      billingType: row.billingType || "",
      billingRate: row.billingRate || "",
      salaryPaidByCustomer: row.salaryPaidByCustomer || "",
      adminExpenditure: row.adminExpenditure || "",
      billedToCustomer: row.billedToCustomer || "",
      remarks: row.remarks || "",
      endClient: row.endClient || "",
      reportingManagerName: row.reportingManagerName || "",
      reportingManagerEmail: row.reportingManagerEmail || "",
      billingStatus: row.billingStatus || "",
      employmentType: row.employmentType || "",
      poEntries: Array.isArray(row.poEntries) && row.poEntries.length > 0
        ? row.poEntries.map((pe) => ({
            id: pe.id || undefined,
            poNumber: pe.poNumber || "",
            poStartDate: pe.poStartDate || "",
            poEndDate: pe.poEndDate || "",
            status: pe.status || "Active",
            benchStartDate: pe.benchStartDate || "",
            remarks: pe.remarks || "",
            attachmentFile: null,
            attachmentExisting: pe.attachmentUrl ? { url: pe.attachmentUrl, name: pe.attachmentOriginalName } : null,
          }))
        : [{ poNumber: "", poStartDate: "", poEndDate: "", status: "Active", benchStartDate: "", remarks: "", attachmentFile: null, attachmentExisting: null }],
      billingEntries: Array.isArray(row.billingEntries) && row.billingEntries.length > 0
        ? row.billingEntries.map((be) => ({
            id: be.id || undefined,
            customerName: be.customerName || "",
            endClient: be.endClient || "",
            billingType: be.billingType || "",
            billingStatus: be.billingStatus || "",
            billingRate: be.billingRate ?? "",
            salaryPaidByCustomer: be.salaryPaidByCustomer ?? "",
            adminExpenditure: be.adminExpenditure ?? "",
            billedToCustomer: be.billedToCustomer ?? "",
          }))
        : [{ customerName: row.customerName || "", endClient: row.endClient || "", billingType: row.billingType || "", billingStatus: row.billingStatus || "", billingRate: row.billingRate ?? "", salaryPaidByCustomer: row.salaryPaidByCustomer ?? "", adminExpenditure: row.adminExpenditure ?? "", billedToCustomer: row.billedToCustomer ?? "" }],
    };
    formSnapshotRef.current = JSON.parse(JSON.stringify(newData));
    setFormData(newData);
    setEditingId(row.id);
    setShowForm(true);
  };

  const savePayrollEntry = async () => {
    if (!canEdit) return;
    setError("");
    setLoading(true);

    const payload = {
      employeeId: null,
      employeeNumber: formData.employeeNumber,
      refId: formData.refId || formData.employeeNumber,
      name: formData.name,
      doj: formData.doj,
      mobileNo: formData.mobileNo,
      ptDate: formData.ptDate,
      businessUnit: formData.businessUnit,
      division: formData.division,
      department: formData.department,
      designation: formData.designation,
      customerName: formData.billingEntries[0]?.customerName || "",
      location: formData.location,
      billingType: formData.billingEntries[0]?.billingType || "",
      billingRate: Number(formData.billingEntries[0]?.billingRate) || 0,
      salaryPaidByCustomer: Number(formData.billingEntries[0]?.salaryPaidByCustomer) || 0,
      adminExpenditure: Number(formData.billingEntries[0]?.adminExpenditure) || 0,
      billedToCustomer: Number(formData.billingEntries[0]?.billedToCustomer) || 0,
      status: formData.poEntries.length > 0 ? formData.poEntries[0].status : "Active",
      remarks: formData.remarks,
      endClient: formData.billingEntries[0]?.endClient || "",
      reportingManagerName: formData.reportingManagerName,
      reportingManagerEmail: formData.reportingManagerEmail,
      billingStatus: formData.billingEntries[0]?.billingStatus || "",
      employmentType: EMPLOYMENT_TYPES.includes(formData.employmentType) ? formData.employmentType : "",
      deletedAttachmentIds: formData.poEntries.filter((pe) => pe._attachmentDeleted && pe.id).map((pe) => pe.id),
      poEntries: formData.poEntries.map(({ attachmentFile, _attachmentDeleted, ...rest }) => rest),
      billingEntries: formData.billingEntries.map((be) => ({
        customerName: be.customerName || "",
        endClient: be.endClient || "",
        billingType: be.billingType || "",
        billingStatus: be.billingStatus || "",
        billingRate: Number(be.billingRate) || 0,
        salaryPaidByCustomer: Number(be.salaryPaidByCustomer) || 0,
        adminExpenditure: Number(be.adminExpenditure) || 0,
        billedToCustomer: Number(be.billedToCustomer) || 0,
      })),
    };

    // Check if any PO entries have new files to upload
    const hasFiles = formData.poEntries.some((pe) => pe.attachmentFile);

    let apiData;
    if (hasFiles) {
      const fd = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (key === "poEntries" || Array.isArray(value)) {
          fd.append(key, JSON.stringify(value));
        } else {
          fd.append(key, value ?? "");
        }
      });
      const attachmentIndices = [];
      formData.poEntries.forEach((pe, i) => {
        if (pe.attachmentFile) {
          fd.append("poAttachments", pe.attachmentFile);
          attachmentIndices.push(i);
        }
      });
      fd.append("poAttachmentIndices", JSON.stringify(attachmentIndices));
      apiData = fd;
    } else {
      apiData = payload;
    }

    try {
      let savedEntry;
      if (editingId) {
        await trackersAPI.updatePayrollEntry(editingId, apiData);
        savedEntry = { ...payload, id: editingId };
        setPayrollData((prev) => prev.map((item) => (item.id === editingId ? savedEntry : item)));
      } else {
        const response = await trackersAPI.createPayrollEntry(apiData);
        savedEntry = { ...payload, id: response.data?.id || Date.now() };
        setPayrollData((prev) => [savedEntry, ...prev]);
      }

      setShowForm(false);
      setViewingOnly(false);
      formSnapshotRef.current = null;
      setLastInfo(editingId ? "Revenue record updated." : "Revenue record saved.");
      await loadPayrollEntries();
    } catch (err) {
      console.error("Failed to save payroll tracker entry", err);
      setError(getErrorMessage(err, "Unable to save record. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handlePoFileChange = (poIndex, file) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("File too large. Maximum size is 10MB.");
      return;
    }
    const allowedTypes = [
      "application/pdf", "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg", "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setError("Only PDF, DOC, DOCX, XLSX, XLS, JPG, PNG files are allowed.");
      return;
    }
    setError("");
    const entries = [...formData.poEntries];
    const hadExisting = !!(entries[poIndex].id && entries[poIndex].attachmentExisting);
    entries[poIndex] = {
      ...entries[poIndex],
      attachmentFile: file,
      _attachmentDeleted: hadExisting || entries[poIndex]._attachmentDeleted,
    };
    setFormData((prev) => ({ ...prev, poEntries: entries }));
  };

  const handleRemovePoFile = (poIndex) => {
    const entries = [...formData.poEntries];
    const entry = entries[poIndex];
    if (entry.id && entry.attachmentExisting) {
      entries[poIndex] = { ...entry, attachmentFile: null, _attachmentDeleted: false };
    } else {
      entries[poIndex] = { ...entry, attachmentFile: null, attachmentExisting: null };
    }
    setFormData((prev) => ({ ...prev, poEntries: entries }));
  };

  const handleDownloadPoAttachment = async (poEntryId, attachmentExisting) => {
    try {
      if (attachmentExisting?.downloadUrl) {
        window.open(attachmentExisting.downloadUrl, "_blank", "noopener,noreferrer");
      } else if (poEntryId) {
        const res = await trackersAPI.downloadPoAttachment(poEntryId);
        const { url, fileName } = res.data;
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.download = fileName || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (attachmentExisting?.url) {
        window.open(attachmentExisting.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Download failed", err);
      setError("Failed to download attachment.");
    }
  };

  const handleDeletePoAttachment = (poIndex, poEntryId) => {
    setAttachmentToDelete({ poIndex, poEntryId });
  };

  const confirmDeletePoAttachment = () => {
    if (!attachmentToDelete) return;
    const { poIndex, poEntryId } = attachmentToDelete;
    const entries = [...formData.poEntries];
    if (poEntryId) {
      entries[poIndex] = { ...entries[poIndex], _attachmentDeleted: true, attachmentExisting: null, attachmentFile: null };
    } else {
      entries[poIndex] = { ...entries[poIndex], attachmentExisting: null, attachmentFile: null };
    }
    setFormData((prev) => ({ ...prev, poEntries: entries }));
    setLastInfo("Attachment removed.");
    setAttachmentToDelete(null);
  };

  const handleExport = async (format, params = {}) => {
    try {
      const res = await trackersAPI.exportTracker("payroll", format, params);
      const isCsv = format === "csv";
      const blob = new Blob([res.data], { type: isCsv ? "text/csv;charset=utf-8" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const suffix = reportTab === "revenue" ? "_revenue_record" : "_employee_record";
      link.setAttribute("download", `revenue_tracker_export${suffix}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setLastInfo("Export downloaded successfully.");
    } catch (err) {
      console.error("Export failed", err);
      setError("Failed to export data.");
    }
  };

  const handleExportCurrentView = (format) => {
    const params = { roster: true };
    if (reportTab === "revenue") params.hasRecord = true;
    handleExport(format, params);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilter1("");
    setFilter2("");
    setFilter3("");
    setFilterBillingStatus("");
    setFilterEmploymentType("");
    setFilterCustomer("");
  };

  const activeFilterCount = [searchTerm, filter1, filter2, filter3, filterBillingStatus, filterEmploymentType, filterCustomer].filter(Boolean).length;

  return (
    <div className="page payroll-tracker">
      <div className="tracker-container">
        <div className="tracker-topbar">
          <div className="tracker-title-section">
            <h1>Revenue Tracker</h1>
            <p className="tracker-subtitle">Track revenue from employees seconded to customers</p>
          </div>
          <div className="tracker-top-actions" style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap", gap: "10px", alignItems: "center" }}>
            {canEdit && (
              <button
                className="btn btn-primary btn-topbar"
                onClick={handleAddNew}
                style={{ height: 36, padding: "0 16px", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: "normal", fontSize: 13, boxSizing: "border-box", margin: 0, verticalAlign: "middle" }}
              >
                + Add New
              </button>
            )}
            <details className="tracker-export-dropdown" ref={exportDropdownRef}>
              <summary
                className="btn btn-secondary btn-topbar"
                onClick={(event) => { if (!canExport) event.preventDefault(); }}
                style={{ height: 36, padding: "0 16px", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: "normal", fontSize: 13, boxSizing: "border-box", margin: 0, verticalAlign: "middle" }}
              >Export ▾</summary>
              <div className="tracker-export-menu" role="menu">
                <button type="button" role="menuitem" disabled={!canExport} onClick={(event) => { event.currentTarget.closest("details").open = false; handleExportCurrentView("xlsx"); }}>Excel</button>
                <button type="button" role="menuitem" disabled={!canExport} onClick={(event) => { event.currentTarget.closest("details").open = false; handleExportCurrentView("csv"); }}>CSV</button>
              </div>
            </details>
          </div>
        </div>

        <div className="tracker-filters tracker-filters-top">
          <div className="filter-group filter-group-search">
            <input type="text" placeholder="Search by Emp No, Name, Customer, PO..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="filter-input" />
          </div>
          <button className="btn btn-secondary tracker-filters-toggle" onClick={() => setShowFilters(!showFilters)}>
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""} {showFilters ? "▲" : "▼"}
          </button>
        </div>

        {showFilters && (
        <div className="tracker-filters">
          <div className="filter-group">
            <label>Status</label>
            <select className="filter-input" value={filter1} onChange={(e) => setFilter1(e.target.value)}>
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Billing Type</label>
            <select className="filter-input" value={filter2} onChange={(e) => setFilter2(e.target.value)}>
              <option value="">All</option>
              {BILLING_TYPES.map((bt) => (<option key={bt} value={bt}>{bt}</option>))}
            </select>
          </div>
          <div className="filter-group">
            <label>Business Unit</label>
            <select className="filter-input" value={filter3} onChange={(e) => setFilter3(e.target.value)}>
              <option value="">All</option>
              {businessUnitOptions.map((b) => (<option key={b} value={b}>{b}</option>))}
            </select>
          </div>
          <div className="filter-group">
            <label>Billing Status</label>
            <select className="filter-input" value={filterBillingStatus} onChange={(e) => setFilterBillingStatus(e.target.value)}>
              <option value="">All</option>
              {BILLING_STATUSES.map((bs) => (<option key={bs} value={bs}>{bs}</option>))}
            </select>
          </div>
          <div className="filter-group">
            <label>Employment Type</label>
            <select className="filter-input" value={filterEmploymentType} onChange={(e) => setFilterEmploymentType(e.target.value)}>
              <option value="">All</option>
              {EMPLOYMENT_TYPES.map((et) => (<option key={et} value={et}>{et}</option>))}
            </select>
          </div>
          <div className="filter-group">
            <label>Customer</label>
            <select className="filter-input" value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)}>
              <option value="">All</option>
              {customerOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>
          <button className="btn btn-clear-filters" onClick={handleClearFilters}>Clear Filters</button>
        </div>
        )}

        <div className="tracker-tabs">
          {[
            { val: "all", label: "Employee Record", count: payrollData.length },
            { val: "revenue", label: "Revenue Record", count: payrollData.filter((r) => r.hasRecord).length },
          ].map(({ val, label, count }) => (
            <button
              key={val}
              className={`tracker-tab ${reportTab === val ? "tracker-tab-active" : ""}`}
              onClick={() => setReportTab(val)}
            >
              <span className="tracker-tab-icon">
                {val === "all" ? (
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" />
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  </svg>
                )}
              </span>
              {label}
              <span className="tracker-tab-count">{count}</span>
            </button>
          ))}
        </div>

        {error && <div className="error-banner">{error}</div>}
        {loading && <div className="loading-banner">Loading…</div>}

        <div className="tracker-card">
          <div className="tracker-card-header">
            <div className="tracker-card-title">
              <h2>Revenue Records</h2>
              <span className="tracker-count">{filteredData.length} record{filteredData.length !== 1 ? "s" : ""}</span>
            </div>
          </div>

          <div className="tracker-table-wrapper">
            <table className="tracker-table">
              <thead>
                <tr>
                  <th scope="col">Emp No</th>
                  <th scope="col">Name</th>
                  <th scope="col">Customer</th>
                  <th scope="col">End Client</th>
                  <th scope="col">Billing Type</th>
                  <th scope="col">Billing Rate</th>
                  <th scope="col">Amount Billed</th>
                  <th scope="col">PO Number</th>
                  <th scope="col">Location</th>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageData.length > 0 ? (
                  pageData.map((row) => {
                    const poEntries = row.poEntries || [];
                    const billingEntries = row.billingEntries || [];
                    const latestPo = poEntries.length > 0 ? poEntries[poEntries.length - 1] : null;
                    const isExpanded = !!expandedRows[row.id];
                    return (
                      <React.Fragment key={row.employeeNumber || row.id}>
                        <tr>
                          <td className="td-emp">{row.employeeNumber}</td>
                          <td className="td-name">{row.name}</td>
                          <td>{row.customerName || "-"}</td>
                          <td>{row.endClient || "-"}</td>
                          <td>{row.billingType || "-"}</td>
                          <td className="td-amount">₹{Number(row.billingRate || 0).toLocaleString()}</td>
                          <td className="td-amount td-billed">₹{Number(row.billedToCustomer || 0).toLocaleString()}</td>
                          <td>
                            {latestPo ? latestPo.poNumber || "-" : "-"}
                            {(poEntries.length > 1 || billingEntries.length > 1) && (
                              <button
                                className="po-expand-btn"
                                onClick={() => setExpandedRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
                                title={isExpanded ? "Collapse" : `Show all POs & Billing`}
                              >
                                {isExpanded ? "▾" : `▴ ${poEntries.length > 1 ? poEntries.length : billingEntries.length}`}
                              </button>
                            )}
                          </td>
                          <td>{row.location || "-"}</td>
                          <td>
                            {!row.hasRecord ? (
                              <span className="status-chip status-no-record">No record</span>
                            ) : (
                              <span className={`status-chip status-${(latestPo?.status || row.status || "active").toLowerCase()}`}>
                                {latestPo?.status || row.status}
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-action btn-view" onClick={() => handleViewRow(row)}>View</button>
                {!viewingOnly && canEdit && (
                                <button className="btn-action" onClick={() => handleEditRow(row)}>Edit</button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (poEntries.length > 1 || billingEntries.length > 1) && (
                          <tr className="po-expanded-row">
                            <td colSpan={11}>
                              <div className="po-sub-table-wrapper">
                                {poEntries.length > 1 && (
                                  <table className="po-sub-table">
                                    <thead>
                                      <tr>
                                        <th scope="col">#</th>
                                        <th scope="col">PO Number</th>
                                        <th scope="col">PO Start</th>
                                        <th scope="col">PO End</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Bench Start</th>
                                        <th scope="col">Remarks</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {poEntries.map((pe, idx) => (
                                        <tr key={pe.id || idx}>
                                          <td>{idx + 1}</td>
                                          <td>{pe.poNumber || "-"}</td>
                                          <td>{pe.poStartDate || "-"}</td>
                                          <td>{pe.poEndDate || "-"}</td>
                                          <td>
                                            <span className={`status-chip status-${(pe.status || "active").toLowerCase()}`}>
                                              {pe.status || "Active"}
                                            </span>
                                          </td>
                                          <td>{pe.benchStartDate || "-"}</td>
                                          <td>{pe.remarks || "-"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                                {billingEntries.length > 1 && (
                                  <table className="po-sub-table billing-sub-table">
                                    <thead>
                                      <tr>
                                        <th scope="col">#</th>
                                        <th scope="col">Customer</th>
                                        <th scope="col">End Client</th>
                                        <th scope="col">Billing Type</th>
                                        <th scope="col">Billing Status</th>
                                        <th scope="col">Billing Rate</th>
                                        <th scope="col">Employee Cost</th>
                                        <th scope="col">Admin Cost</th>
                                        <th scope="col">Amount Billed</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {billingEntries.map((be, idx) => (
                                        <tr key={be.id || idx}>
                                          <td>{idx + 1}</td>
                                          <td>{be.customerName || "-"}</td>
                                          <td>{be.endClient || "-"}</td>
                                          <td>{be.billingType || "-"}</td>
                                          <td>{be.billingStatus || "-"}</td>
                                          <td className="td-amount">₹{Number(be.billingRate || 0).toLocaleString()}</td>
                                          <td className="td-amount">₹{Number(be.salaryPaidByCustomer || 0).toLocaleString()}</td>
                                          <td className="td-amount">₹{Number(be.adminExpenditure || 0).toLocaleString()}</td>
                                          <td className="td-amount">₹{Number(be.billedToCustomer || 0).toLocaleString()}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="no-data">No revenue records found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="btn btn-pagination" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>&#9664; Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={`btn btn-pagination ${p === page ? "btn-pagination-active" : ""}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="btn btn-pagination" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next &#9654;</button>
              <span className="pagination-info">{filteredData.length} records</span>
            </div>
          )}
        </div>

        {lastInfo && <div className="info-banner">{lastInfo}</div>}

        {showForm && (
          <div className="modal-overlay" onClick={confirmClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{viewingOnly ? "View" : editingId ? "Edit" : "Add"} Revenue Record</h2>
                <button className="close-btn" onClick={confirmClose}>✕</button>
              </div>

              <div className="modal-body">
                <div className="form-section" ref={employeeDropdownRef} style={{ position: "relative" }}>
                  <label className="employee-number-label">Employee Number *</label>
                  <input
                    type="text"
                    name="employeeNumber"
                    value={formData.employeeNumber}
                    onChange={handleEmployeeSearchChange}
                    onFocus={() => { if (formData.employeeNumber && !editingId) { setShowEmployeeDropdown(true); loadEmployeeOptions(formData.employeeNumber); } }}
                    placeholder="Type employee number or name to search..."
                    className="input-employee-number"
                    disabled={!canEdit || viewingOnly}
                    autoComplete="off"
                  />
                  <p className="form-instruction">Type employee number or name to search and auto-populate from Zoho People.</p>
                  {showEmployeeDropdown && (
                    <div className="employee-dropdown">
                      <div className="employee-dropdown-filters">
                        <span className="employee-dropdown-filters-label">Status</span>
                        <button type="button" className={`employee-dropdown-filter-btn ${empStatusFilter === '' ? 'active' : ''}`} onClick={() => setEmpStatusFilter('')}>All</button>
                        <button type="button" className={`employee-dropdown-filter-btn ${empStatusFilter === 'active' ? 'active' : ''}`} onClick={() => setEmpStatusFilter('active')}>Active</button>
                        <button type="button" className={`employee-dropdown-filter-btn ${empStatusFilter === 'inactive' ? 'inactive' : ''}`} onClick={() => setEmpStatusFilter('inactive')}>Inactive</button>
                      </div>
                      {employeeOptions.length > 0 ? (
                        employeeOptions.map((emp) => (
                          <div key={emp.employee_id} className="employee-dropdown-item" onClick={() => selectEmployee(emp.employee_id)}>
                            <span>
                              <strong>{emp.employee_id}</strong> — {emp.name}
                              {emp.department ? ` (${emp.department})` : ""}
                            </span>
                            {emp.status ? <span className="emp-status-tag">{emp.status}</span> : null}
                          </div>
                        ))
                      ) : (
                        <div className="employee-dropdown-empty">No employees found.</div>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-section-divider">
                  <span className="divider-label">Employee Details</span>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Ref ID</label>
                    <input type="text" name="refId" value={formData.refId || formData.employeeNumber} className="input-field" disabled />
                  </div>
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>DOJ</label>
                    <input type="date" name="doj" value={formData.doj} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>Mobile No</label>
                    <input type="text" name="mobileNo" value={formData.mobileNo} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>PT Date</label>
                    <input type="date" name="ptDate" value={formData.ptDate} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>Business Unit</label>
                    <select name="businessUnit" value={formData.businessUnit} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly}>
                      <option value="">Select</option>
                      {businessUnitOptions.map((b) => (<option key={b} value={b}>{b}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Division</label>
                    <select name="division" value={formData.division} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly}>
                      <option value="">Select</option>
                      {divisionOptions.map((d) => (<option key={d} value={d}>{d}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Department</label>
                    <input type="text" name="department" value={formData.department} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>Designation</label>
                    <input type="text" name="designation" value={formData.designation} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" name="location" value={formData.location} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>Employment Type</label>
                    <select name="employmentType" value={formData.employmentType} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly}>
                      <option value="">Select</option>
                      {EMPLOYMENT_TYPES.map((et) => (<option key={et} value={et}>{et}</option>))}
                    </select>
                  </div>
                </div>

                <div className="form-section-divider">
                  <span className="divider-label">Reporting Manager</span>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Reporting Manager Name</label>
                    <input type="text" name="reportingManagerName" value={formData.reportingManagerName} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                  <div className="form-group">
                    <label>Reporting Manager Email</label>
                    <input type="email" name="reportingManagerEmail" value={formData.reportingManagerEmail} onChange={handleFormChange} className="input-field" disabled={!canEdit || viewingOnly} />
                  </div>
                </div>

                <div className="form-section-divider">
                  <span className="divider-label">Billing & Customer</span>
                </div>

                {formData.billingEntries.map((be, i) => (
                  <div key={i} className="po-entry-block">
                    <div className="po-entry-header">
                      <span className="po-entry-label">Billing & Customer {i + 1}</span>
                      {formData.billingEntries.length > 1 && !viewingOnly && (
                        <button
                          type="button"
                          className="po-entry-remove"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              billingEntries: prev.billingEntries.filter((_, idx) => idx !== i),
                            }));
                          }}
                        >
                          × Remove
                        </button>
                      )}
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Customer Name</label>
                        <input type="text" value={be.customerName} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], customerName: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>End Client</label>
                        <input type="text" value={be.endClient} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], endClient: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" placeholder="Free text" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>Billing Type *</label>
                        <select value={be.billingType} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], billingType: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly}>
                          <option value="">Select</option>
                          {BILLING_TYPES.map((bt) => (<option key={bt} value={bt}>{bt}</option>))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Billing Status</label>
                        <select value={be.billingStatus} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], billingStatus: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly}>
                          <option value="">Select</option>
                          {BILLING_STATUSES.map((bs) => (<option key={bs} value={bs}>{bs}</option>))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Billing Rate</label>
                        <input type="number" value={be.billingRate} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], billingRate: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" placeholder="Only number" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>Employee Cost</label>
                        <input type="number" value={be.salaryPaidByCustomer} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], salaryPaidByCustomer: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" placeholder="Only number" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>Administrative Cost</label>
                        <input type="number" value={be.adminExpenditure} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], adminExpenditure: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" placeholder="Only number" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>Amount Billed to Customer</label>
                        <input type="number" value={be.billedToCustomer} onChange={(e) => {
                          const entries = [...formData.billingEntries];
                          entries[i] = { ...entries[i], billedToCustomer: e.target.value };
                          setFormData((prev) => ({ ...prev, billingEntries: entries }));
                        }} className="input-field" placeholder="Only number" disabled={!canEdit || viewingOnly} />
                      </div>
                    </div>
                  </div>
                ))}

                {!viewingOnly && canEdit && (
                  <button
                    type="button"
                    className="btn btn-secondary po-add-btn"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        billingEntries: [...prev.billingEntries, { customerName: "", endClient: "", billingType: "", billingStatus: "", billingRate: "", salaryPaidByCustomer: "", adminExpenditure: "", billedToCustomer: "" }],
                      }));
                    }}
                  >
                    + Add Billing & Customer
                  </button>
                )}

                <div className="form-section-divider">
                  <span className="divider-label">PO & Status</span>
                </div>

                {formData.poEntries.map((entry, i) => (
                  <div key={i} className="po-entry-block">
                    <div className="po-entry-header">
                      <span className="po-entry-label">PO {i + 1}</span>
                      {formData.poEntries.length > 1 && !viewingOnly && (
                        <button
                          type="button"
                          className="po-entry-remove"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              poEntries: prev.poEntries.filter((_, idx) => idx !== i),
                            }));
                          }}
                        >
                          × Remove
                        </button>
                      )}
                    </div>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>PO Number</label>
                        <input type="text" value={entry.poNumber} onChange={(e) => {
                          const entries = [...formData.poEntries];
                          entries[i] = { ...entries[i], poNumber: e.target.value };
                          setFormData((prev) => ({ ...prev, poEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>PO Start Date</label>
                        <input type="date" value={entry.poStartDate} onChange={(e) => {
                          const entries = [...formData.poEntries];
                          entries[i] = { ...entries[i], poStartDate: e.target.value };
                          setFormData((prev) => ({ ...prev, poEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>PO End Date</label>
                        <input type="date" value={entry.poEndDate} onChange={(e) => {
                          const entries = [...formData.poEntries];
                          entries[i] = { ...entries[i], poEndDate: e.target.value };
                          setFormData((prev) => ({ ...prev, poEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select value={entry.status} onChange={(e) => {
                          const entries = [...formData.poEntries];
                          entries[i] = { ...entries[i], status: e.target.value };
                          setFormData((prev) => ({ ...prev, poEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly}>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Bench Start Date</label>
                        <input type="date" value={entry.benchStartDate} onChange={(e) => {
                          const entries = [...formData.poEntries];
                          entries[i] = { ...entries[i], benchStartDate: e.target.value };
                          setFormData((prev) => ({ ...prev, poEntries: entries }));
                        }} className="input-field" disabled={!canEdit || viewingOnly} />
                      </div>
                      <div className="form-group">
                        <label>Remarks</label>
                        <textarea value={entry.remarks} onChange={(e) => {
                          const entries = [...formData.poEntries];
                          entries[i] = { ...entries[i], remarks: e.target.value };
                          setFormData((prev) => ({ ...prev, poEntries: entries }));
                        }} className="input-field" placeholder="Free text" disabled={!canEdit || viewingOnly} />
                      </div>
                    </div>

                    {/* PO Attachment Section */}
                    <div className="po-attachment-section">
                      <label className="po-attachment-label">PO Document</label>
                      {entry.attachmentExisting && !entry.attachmentFile ? (
                        <div className="po-attachment-file">
                          <span className="po-attachment-icon">&#128196;</span>
                          <span className="po-attachment-name">{entry.attachmentExisting.name}</span>
                          <button type="button" className="po-attachment-view" onClick={() => handleDownloadPoAttachment(entry.id, entry.attachmentExisting)} title="Download">&#8599;</button>
                          {!viewingOnly && canEdit && (
                            <button type="button" className="po-attachment-delete" onClick={entry.id ? () => handleDeletePoAttachment(i, entry.id) : () => handleRemovePoFile(i)} title="Remove">&#10005;</button>
                          )}
                        </div>
                      ) : entry.attachmentFile ? (
                        <div className="po-attachment-file po-attachment-new">
                          <span className="po-attachment-icon">&#128196;</span>
                          <span className="po-attachment-name">{entry.attachmentFile.name}</span>
                          <span className="po-attachment-badge">New</span>
                          <button type="button" className="po-attachment-delete" onClick={() => handleRemovePoFile(i)} title="Remove">&#10005;</button>
                        </div>
                      ) : !viewingOnly && canEdit ? (
                        <label className="po-attachment-upload">
                          <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={(e) => handlePoFileChange(i, e.target.files[0])} style={{ display: "none" }} />
                          <span className="po-attachment-upload-text">&#128206; Click to attach PO document</span>
                          <span className="po-attachment-hint">PDF, DOC, XLSX, JPG, PNG (max 10MB)</span>
                        </label>
                      ) : (
                        <span className="po-attachment-none">No attachment</span>
                      )}
                    </div>
                  </div>
                ))}

                {!viewingOnly && canEdit && (
                  <button
                    type="button"
                    className="btn btn-secondary po-add-btn"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        poEntries: [...prev.poEntries, { poNumber: "", poStartDate: "", poEndDate: "", status: "Active", benchStartDate: "", remarks: "", attachmentFile: null, attachmentExisting: null }],
                      }));
                    }}
                  >
                    + Add PO
                  </button>
                )}
              </div>

              <div className="modal-footer">
                {!viewingOnly && canEdit && <button className="btn btn-save" disabled={loading} onClick={() => savePayrollEntry()}>{loading ? "Saving..." : "Save"}</button>}
                <button className="btn btn-cancel" onClick={confirmClose}>{viewingOnly ? "Close" : "Cancel"}</button>
              </div>
            </div>
          </div>
        )}

      <ConfirmModal
        isOpen={!!attachmentToDelete}
        variant="danger"
        title="Delete attachment?"
        message="Are you sure you want to delete this attachment?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDeletePoAttachment}
        onCancel={() => setAttachmentToDelete(null)}
      />
      </div>
    </div>
  );
}
